// LogosIQ Markdown Renderer
// Rich text formatting for chat messages with memory references and styled elements

"use client"

import React from "react"
import { cn } from "@/lib/utils"
import {
  BookOpen,
  ExternalLink,
  Quote,
  Lightbulb,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Database,
  Edit2,
  Check,
  X,
  Shield,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useState } from "react"
import { ExternalSourceLink } from "@/components/citations/external-source-link"
import { MemoryCitationPill } from "@/components/citations/memory-citation-pill"
import { FF_CITATIONS_UI_ENABLED } from "@/lib/config/feature-flags"

interface MarkdownRendererProps {
  content: string
  className?: string
  mode?: "profile" | "advisory"
  variant?: "user" | "advisor"
  onCardSave?: (section: string, field: string, value: string) => void
  onCardDismiss?: (section: string, field: string) => void
  showCitations?: boolean
}

// Custom component for memory references - shows where data came from
function MemoryReference({ section, field, onClick }: { section: string; field?: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full",
        "bg-teal-900/40 border border-teal-700/30 text-teal-300 text-xs font-medium",
        "hover:bg-teal-800/50 hover:border-teal-600/50 transition-colors cursor-pointer",
      )}
    >
      <BookOpen className="h-3 w-3" />
      <span>{section}</span>
      {field && (
        <>
          <ArrowRight className="h-2.5 w-2.5 text-teal-500" />
          <span>{field}</span>
        </>
      )}
    </button>
  )
}

interface MemoryCitation {
  section: string
  field?: string
}

function MemoryCitationsList({ citations }: { citations: MemoryCitation[] }) {
  if (citations.length === 0) return null

  return (
    <div className="mt-4 pt-3 border-t border-slate-700/50">
      <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
        <Database className="h-3 w-3" />
        Sources from your profile:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {citations.map((citation, i) => (
          <MemoryReference key={i} section={citation.section} field={citation.field} />
        ))}
      </div>
    </div>
  )
}

// ProfileCard component for rendering saveable cards
interface ProfileCardProps {
  section: string
  field: string
  title: string
  value: string
  confidence?: "high" | "medium" | "low"
  onSave?: (value: string) => void
  onDismiss?: () => void
}

function ConfidenceIndicator({ level }: { level: "high" | "medium" | "low" }) {
  const config = {
    high: { label: "High confidence", color: "text-teal-400", bars: 3 },
    medium: { label: "Medium confidence", color: "text-amber-400", bars: 2 },
    low: { label: "Needs review", color: "text-orange-400", bars: 1 },
  }
  const { label, color, bars } = config[level]

  return (
    <div className="flex items-center gap-1.5" title={label}>
      <Shield className={`h-3 w-3 ${color}`} />
      <div className="flex gap-0.5">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`w-1 h-2 rounded-sm ${i <= bars ? color.replace("text-", "bg-") : "bg-slate-600"}`} />
        ))}
      </div>
      <span className={`text-xs ${color}`}>{label}</span>
    </div>
  )
}

function ProfileCard({ section, field, title, value, confidence, onSave, onDismiss }: ProfileCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedValue, setEditedValue] = useState(value)
  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)

  const handleSave = () => {
    setIsSaving(true)
    setTimeout(() => {
      onSave?.(editedValue)
      setIsSaving(false)
      setIsSaved(true)
      setTimeout(() => setIsSaved(false), 2000)
    }, 300)
  }

  const isLongText = value.length > 100

  return (
    <div
      className={`rounded-lg border overflow-hidden transition-all duration-300 ${
        isSaved ? "border-teal-500 bg-teal-950/40" : "border-teal-700/40 bg-slate-900/60"
      }`}
    >
      <div className="px-4 py-3 border-b border-teal-700/30 bg-teal-950/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-teal-400" />
            <span className="text-sm font-medium text-teal-300">{title}</span>
          </div>
          {confidence && <ConfidenceIndicator level={confidence} />}
        </div>
        <div className="mt-1 text-xs text-slate-500 flex items-center gap-1">
          <ArrowRight className="h-3 w-3" />
          <span>
            {section} → {field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </span>
        </div>
      </div>

      <div className="p-4">
        {isEditing ? (
          isLongText ? (
            <Textarea
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              className="min-h-[100px] bg-slate-800/50 border-slate-700 text-slate-200"
              autoFocus
            />
          ) : (
            <Input
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              className="bg-slate-800/50 border-slate-700 text-slate-200"
              autoFocus
            />
          )
        ) : (
          <p className="text-sm text-slate-200 leading-relaxed">{editedValue}</p>
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-700/50 bg-slate-900/40 flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={isSaving || isSaved}
          className={`flex-1 transition-all duration-300 ${
            isSaved
              ? "border-teal-500 text-teal-400 bg-teal-950/50"
              : "border-teal-600 text-teal-400 hover:bg-teal-950 hover:text-teal-300 bg-transparent"
          }`}
        >
          {isSaving ? (
            <>
              <div className="h-4 w-4 mr-1.5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : isSaved ? (
            <>
              <Check className="h-4 w-4 mr-1.5" />
              Saved!
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-1.5" />
              Save to Profile
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsEditing(!isEditing)}
          disabled={isSaving}
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          <Edit2 className="h-4 w-4 mr-1.5" />
          Edit
        </Button>
        {onDismiss && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            disabled={isSaving}
            className="text-slate-500 hover:text-slate-300"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}

const TYPOGRAPHY = {
  user: {
    paragraph: "font-sans text-[18px] leading-[1.55] font-normal text-slate-300",
    listItem: "font-sans text-[18px] leading-[1.55] font-normal text-slate-300",
    blockquote: "font-sans text-[18px] leading-[1.55] font-normal text-slate-400 italic",
    callout: "font-sans text-[18px] leading-[1.55] font-normal",
  },
  advisor: {
    paragraph: "text-sm text-slate-300 leading-relaxed",
    listItem: "text-sm text-slate-300",
    blockquote: "text-sm text-slate-400 italic",
    callout: "text-sm",
  },
} as const

// Parse and render markdown content
export function MarkdownRenderer({
  content,
  className,
  mode = "profile",
  variant = "advisor",
  onCardSave,
  onCardDismiss,
  showCitations = false,
}: MarkdownRendererProps) {
  const elements = parseMarkdown(content)
  const citations = showCitations && mode === "advisory" ? extractCitations(content) : []

  return (
    <div className={cn("markdown-content space-y-3", className)}>
      {elements.map((element, index) => (
        <React.Fragment key={index}>
          {renderElement(element, index, mode, variant, onCardSave, onCardDismiss)}
        </React.Fragment>
      ))}
      {mode === "advisory" && citations.length > 0 && <MemoryCitationsList citations={citations} />}
    </div>
  )
}

// Types for parsed elements
type MarkdownElement =
  | { type: "h1"; content: string }
  | { type: "h2"; content: string }
  | { type: "h3"; content: string }
  | { type: "h4"; content: string }
  | { type: "paragraph"; content: string }
  | { type: "blockquote"; content: string }
  | { type: "pullquote"; content: string; attribution?: string }
  | { type: "insight"; content: string }
  | { type: "warning"; content: string }
  | { type: "success"; content: string }
  | { type: "memory-ref"; section: string; field?: string }
  | { type: "divider" }
  | {
      type: "profile-card"
      section: string
      field: string
      title: string
      value: string
      confidence?: "high" | "medium" | "low"
    }

// Parse markdown string into elements
function parseMarkdown(content: string): MarkdownElement[] {
  const lines = content.split("\n")
  const elements: MarkdownElement[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i].trim()

    // Skip empty lines
    if (!line) {
      i++
      continue
    }

    if (line === ":::card") {
      const cardLines: string[] = []
      i++
      while (i < lines.length && lines[i].trim() !== ":::") {
        cardLines.push(lines[i].trim())
        i++
      }
      if (lines[i]?.trim() === ":::") i++

      // Parse the collected card lines
      const cardData: Record<string, string> = {}
      for (const cardLine of cardLines) {
        const colonIndex = cardLine.indexOf(":")
        if (colonIndex > 0) {
          const key = cardLine.slice(0, colonIndex).trim()
          const val = cardLine.slice(colonIndex + 1).trim()
          cardData[key] = val
        }
      }

      if (cardData.section && cardData.field && cardData.title) {
        elements.push({
          type: "profile-card",
          section: cardData.section,
          field: cardData.field,
          title: cardData.title,
          value: cardData.value || "",
          confidence: (cardData.confidence as "high" | "medium" | "low") || undefined,
        })
      }
      continue
    }

    // Divider
    if (line.match(/^-{3,}$/) || line.match(/^\*{3,}$/) || line.match(/^_{3,}$/)) {
      elements.push({ type: "divider" })
      i++
      continue
    }

    // Headers
    if (line.startsWith("#### ")) {
      elements.push({ type: "h4", content: line.slice(5) })
      i++
      continue
    }
    if (line.startsWith("### ")) {
      elements.push({ type: "h3", content: line.slice(4) })
      i++
      continue
    }
    if (line.startsWith("## ")) {
      elements.push({ type: "h2", content: line.slice(3) })
      i++
      continue
    }
    if (line.startsWith("# ")) {
      elements.push({ type: "h1", content: line.slice(2) })
      i++
      continue
    }

    // Special blocks: insight, warning, success
    if (line.startsWith(":::insight") || line.startsWith("> [!insight]") || line.startsWith("> 💡")) {
      const blockContent: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith(":::") && lines[i].trim() !== "") {
        blockContent.push(lines[i].replace(/^>\s*/, "").trim())
        i++
      }
      if (lines[i]?.trim() === ":::") i++
      elements.push({ type: "insight", content: blockContent.join(" ") })
      continue
    }

    if (line.startsWith(":::warning") || line.startsWith("> [!warning]") || line.startsWith("> ⚠️")) {
      const blockContent: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith(":::") && lines[i].trim() !== "") {
        blockContent.push(lines[i].replace(/^>\s*/, "").trim())
        i++
      }
      if (lines[i]?.trim() === ":::") i++
      elements.push({ type: "warning", content: blockContent.join(" ") })
      continue
    }

    if (line.startsWith(":::success") || line.startsWith("> [!success]") || line.startsWith("> ✅")) {
      const blockContent: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith(":::") && lines[i].trim() !== "") {
        blockContent.push(lines[i].replace(/^>\s*/, "").trim())
        i++
      }
      if (lines[i]?.trim() === ":::") i++
      elements.push({ type: "success", content: blockContent.join(" ") })
      continue
    }

    // Pull quote with attribution
    if (line.startsWith('> "') || line.startsWith('">')) {
      const quoteLines: string[] = [line.slice(2)]
      i++
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quoteLines.push(lines[i].replace(/^>\s*/, ""))
        i++
      }
      const fullQuote = quoteLines.join(" ")
      const attributionMatch = fullQuote.match(/—\s*(.+)$/)
      elements.push({
        type: "pullquote",
        content: fullQuote
          .replace(/—\s*.+$/, "")
          .replace(/[""]/g, "")
          .trim(),
        attribution: attributionMatch?.[1],
      })
      continue
    }

    // Regular blockquote
    if (line.startsWith(">")) {
      const quoteLines: string[] = [line.slice(1).trim()]
      i++
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quoteLines.push(lines[i].replace(/^>\s*/, "").trim())
        i++
      }
      elements.push({ type: "blockquote", content: quoteLines.join(" ") })
      continue
    }

    // Memory reference: [[Section::Field]] or [[Section]]
    const memoryMatch = line.match(/^\[\[([^:\]]+)(?:::([^\]]+))?\]\]$/)
    if (memoryMatch) {
      elements.push({ type: "memory-ref", section: memoryMatch[1], field: memoryMatch[2] })
      i++
      continue
    }

    // Bullet list → Convert to stacked paragraphs (Option A)
    if (line.startsWith("- ") || line.startsWith("* ") || line.startsWith("• ")) {
      const content = line.slice(2).trim()
      elements.push({ type: "paragraph", content })
      i++
      continue
    }

    // Numbered list → Convert to stacked paragraphs (Option A)
    if (line.match(/^\d+\.\s/)) {
      const content = line.replace(/^\d+\.\s/, "").trim()
      elements.push({ type: "paragraph", content })
      i++
      continue
    }

    // Regular paragraph
    elements.push({ type: "paragraph", content: line })
    i++
  }

  return elements
}

const LINK_REGEX = /\[([^\]]+)\]$$([^)]+)$$/

// Render inline formatting (bold, italic, underline, links, inline memory refs)
function renderInlineContent(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    // Inline memory reference: [[Section::Field]] or [[Section]]
    const memoryMatch = remaining.match(/\[\[([^:\]]+)(?:::([^\]]+))?\]\]/)
    if (memoryMatch && memoryMatch.index !== undefined) {
      if (memoryMatch.index > 0) {
        parts.push(
          <React.Fragment key={key++}>{renderInlineContent(remaining.slice(0, memoryMatch.index))}</React.Fragment>,
        )
      }
      if (FF_CITATIONS_UI_ENABLED) {
        parts.push(
          <MemoryCitationPill
            key={key++}
            label={memoryMatch[2] ? `${memoryMatch[1]} → ${memoryMatch[2]}` : memoryMatch[1]}
            domain={memoryMatch[1].toLowerCase().replace(/\s+/g, "-")}
            nodeSlug={memoryMatch[2]?.toLowerCase().replace(/\s+/g, "-")}
          />,
        )
      } else {
        parts.push(<MemoryReference key={key++} section={memoryMatch[1]} field={memoryMatch[2]} />)
      }
      remaining = remaining.slice(memoryMatch.index + memoryMatch[0].length)
      continue
    }

    const linkRegex = /\[([^\]]+)\]$$([^)]+)$$/
    const linkMatch = linkRegex.exec(remaining)
    if (linkMatch && linkMatch.index !== undefined) {
      if (linkMatch.index > 0) {
        parts.push(
          <React.Fragment key={key++}>{renderInlineContent(remaining.slice(0, linkMatch.index))}</React.Fragment>,
        )
      }
      if (FF_CITATIONS_UI_ENABLED) {
        parts.push(<ExternalSourceLink key={key++} url={linkMatch[2]} label={linkMatch[1]} />)
      } else {
        parts.push(
          <a
            key={key++}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-teal-400 hover:text-teal-300 underline underline-offset-2"
          >
            {linkMatch[1]}
            <ExternalLink className="h-3 w-3" />
          </a>,
        )
      }
      remaining = remaining.slice(linkMatch.index + linkMatch[0].length)
      continue
    }

    // Bold: **text** or __text__
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/) || remaining.match(/__([^_]+)__/)
    if (boldMatch && boldMatch.index !== undefined) {
      if (boldMatch.index > 0) {
        parts.push(
          <React.Fragment key={key++}>{renderInlineContent(remaining.slice(0, boldMatch.index))}</React.Fragment>,
        )
      }
      const boldText = boldMatch[1]
      const isMemoryTerm = isLikelyMemoryReference(boldText)

      if (FF_CITATIONS_UI_ENABLED && isMemoryTerm) {
        parts.push(<MemoryCitationPill key={key++} label={boldText} domain={inferDomainFromTerm(boldText)} />)
      } else {
        parts.push(
          <strong key={key++} className="font-semibold text-slate-100">
            {boldText}
          </strong>,
        )
      }
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length)
      continue
    }

    // Italic: *text* or _text_
    const italicMatch = remaining.match(/\*([^*]+)\*/) || remaining.match(/_([^_]+)_/)
    if (italicMatch && italicMatch.index !== undefined) {
      if (italicMatch.index > 0) {
        parts.push(
          <React.Fragment key={key++}>{renderInlineContent(remaining.slice(0, italicMatch.index))}</React.Fragment>,
        )
      }
      parts.push(
        <em key={key++} className="italic text-slate-200">
          {italicMatch[1]}
        </em>,
      )
      remaining = remaining.slice(italicMatch.index + italicMatch[0].length)
      continue
    }

    // Underline: ~text~ (custom syntax)
    const underlineMatch = remaining.match(/~([^~]+)~/)
    if (underlineMatch && underlineMatch.index !== undefined) {
      if (underlineMatch.index > 0) {
        parts.push(
          <React.Fragment key={key++}>{renderInlineContent(remaining.slice(0, underlineMatch.index))}</React.Fragment>,
        )
      }
      parts.push(
        <span key={key++} className="underline underline-offset-2">
          {underlineMatch[1]}
        </span>,
      )
      remaining = remaining.slice(underlineMatch.index + underlineMatch[0].length)
      continue
    }

    // Inline code: `code`
    const codeMatch = remaining.match(/`([^`]+)`/)
    if (codeMatch && codeMatch.index !== undefined) {
      if (codeMatch.index > 0) {
        parts.push(
          <React.Fragment key={key++}>{renderInlineContent(remaining.slice(0, codeMatch.index))}</React.Fragment>,
        )
      }
      parts.push(
        <code key={key++} className="px-1.5 py-0.5 rounded bg-slate-800 text-teal-300 text-sm font-mono">
          {codeMatch[1]}
        </code>,
      )
      remaining = remaining.slice(codeMatch.index + codeMatch[0].length)
      continue
    }

    // No more patterns found, add remaining text
    parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>)
    break
  }

  return parts
}

// Render a parsed element to React
function renderElement(
  element: MarkdownElement,
  index: number,
  mode: "profile" | "advisory",
  variant: "user" | "advisor",
  onCardSave?: (section: string, field: string, value: string) => void,
  onCardDismiss?: (section: string, field: string) => void,
): React.ReactNode {
  const typo = TYPOGRAPHY[variant]

  switch (element.type) {
    case "profile-card":
      if (mode === "advisory") {
        return null
      }
      return (
        <ProfileCard
          section={element.section}
          field={element.field}
          title={element.title}
          value={element.value}
          confidence={element.confidence}
          onSave={(value) => onCardSave?.(element.section, element.field, value)}
          onDismiss={() => onCardDismiss?.(element.section, element.field)}
        />
      )

    case "h1":
      return <h1 className="text-xl font-bold text-slate-100 tracking-tight">{renderInlineContent(element.content)}</h1>

    case "h2":
      return (
        <h2 className="text-lg font-semibold text-slate-100 tracking-tight">{renderInlineContent(element.content)}</h2>
      )

    case "h3":
      return (
        <h3 className="text-base font-semibold text-slate-200 tracking-tight">
          {renderInlineContent(element.content)}
        </h3>
      )

    case "h4":
      return (
        <h4 className="text-sm font-medium text-slate-300 uppercase tracking-wide">
          {renderInlineContent(element.content)}
        </h4>
      )

    case "paragraph":
      return <p className={typo.paragraph}>{renderInlineContent(element.content)}</p>

    case "blockquote":
      return (
        <blockquote className={`border-l-2 border-teal-600/50 pl-4 py-1 ${typo.blockquote}`}>
          {renderInlineContent(element.content)}
        </blockquote>
      )

    case "pullquote":
      return (
        <figure className="relative pl-6 py-2">
          <Quote className="absolute left-0 top-2 h-4 w-4 text-teal-500/60" />
          <blockquote className="text-base text-slate-200 italic leading-relaxed">
            "{renderInlineContent(element.content)}"
          </blockquote>
          {element.attribution && (
            <figcaption className="mt-1.5 text-xs text-slate-500">— {element.attribution}</figcaption>
          )}
        </figure>
      )

    case "insight":
      return (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-950/30 border border-amber-800/30">
          <Lightbulb className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className={`${typo.callout} text-amber-200/90 leading-relaxed`}>{renderInlineContent(element.content)}</p>
        </div>
      )

    case "warning":
      return (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-red-950/30 border border-red-800/30">
          <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className={`${typo.callout} text-red-200/90 leading-relaxed`}>{renderInlineContent(element.content)}</p>
        </div>
      )

    case "success":
      return (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-950/30 border border-emerald-800/30">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
          <p className={`${typo.callout} text-emerald-200/90 leading-relaxed`}>
            {renderInlineContent(element.content)}
          </p>
        </div>
      )

    case "memory-ref":
      return <MemoryReference section={element.section} field={element.field} />

    case "divider":
      return <hr className="border-t border-slate-700/50 my-2" />

    default:
      return null
  }
}

function extractCitations(content: string): MemoryCitation[] {
  const citations: MemoryCitation[] = []
  const regex = /\[\[([^:\]]+)(?:::([^\]]+))?\]\]/g
  let match

  while ((match = regex.exec(content)) !== null) {
    citations.push({
      section: match[1],
      field: match[2],
    })
  }

  // Deduplicate
  return citations.filter((c, i, arr) => arr.findIndex((x) => x.section === c.section && x.field === c.field) === i)
}

function parseCardBlock(block: string): MarkdownElement | null {
  const lines = block.split("\n")
  const data: Record<string, string> = {}

  for (const line of lines) {
    const match = line.match(/^(\w+):\s*(.+)$/)
    if (match) {
      data[match[1]] = match[2]
    }
  }

  if (data.section && data.field && data.title) {
    return {
      type: "profile-card",
      section: data.section,
      field: data.field,
      title: data.title,
      value: data.value || "",
      confidence: (data.confidence as "high" | "medium" | "low") || undefined,
    }
  }
  return null
}

function isLikelyMemoryReference(text: string): boolean {
  // Known memory-related terms that should render as pills
  const memoryTerms = [
    "marketing & brand",
    "marketing",
    "brand",
    "sales",
    "operations",
    "finance",
    "strategy",
    "get more clients",
    "grow revenue",
    "reduce costs",
    "improve efficiency",
    "identity",
    "team",
    "audience",
    "competition",
    "offerings",
    "partnerships",
    "technology",
  ]
  return memoryTerms.some((term) => text.toLowerCase().includes(term))
}

function inferDomainFromTerm(text: string): string {
  const termToDomain: Record<string, string> = {
    marketing: "marketing",
    brand: "brand",
    "marketing & brand": "marketing",
    sales: "sales",
    operations: "operations",
    finance: "finance",
    strategy: "strategy",
    "get more clients": "strategy",
    "grow revenue": "finance",
    "reduce costs": "finance",
    "improve efficiency": "operations",
    identity: "identity",
    team: "team",
    audience: "audience",
    competition: "competition",
    offerings: "offerings",
    partnerships: "partnerships",
    technology: "technology",
  }

  const lowerText = text.toLowerCase()
  for (const [term, domain] of Object.entries(termToDomain)) {
    if (lowerText.includes(term)) {
      return domain
    }
  }
  return "strategy" // Default fallback
}
